import { Mail, Instagram, LifeBuoy } from 'lucide-react'
import AtmosphericHero from '@/components/marketing/AtmosphericHero'
import ScrollReveal from '@/components/marketing/ScrollReveal'
import type { SharedMarketingProps } from '@/types/marketing'

interface ContactPageProps extends SharedMarketingProps {
  hero: {
    eyebrow: string
    title: string
    body: string
  }
  channels: {
    title: string
    description: string
    value: string
    href: string
    icon: string
    external: boolean
  }[]
  details: {
    company: string
    contact_number: string
    address: string
    hours: string
    response_time: string
  }
  cta: {
    title: string
    body: string
    actions: { label: string; href: string }[]
  }
}

const iconMap: Record<string, typeof Mail> = {
  mail: Mail,
  support: LifeBuoy,
  instagram: Instagram
}

export default function Contact(props: ContactPageProps) {
  return (
    <>
      <AtmosphericHero minHeight="48vh">
        <div className="mx-auto max-w-3xl text-center space-y-10">
          <span className="hero-eyebrow" style={{ justifyContent: 'center' }}>{props.hero.eyebrow}</span>
          <h1 className="hero-headline" style={{ fontSize: 'clamp(2.5rem, 6vw, 5rem)' }}>{props.hero.title}</h1>
          <p className="hero-body mx-auto">{props.hero.body}</p>
        </div>
      </AtmosphericHero>

      <section className="marketing-section">
        <div className="container mx-auto px-6">
          <div className="grid gap-6 md:grid-cols-3">
            {props.channels.map((channel, index) => {
              const Icon = iconMap[channel.icon] || Mail
              return (
                <ScrollReveal key={channel.title} direction="up" delay={index * 0.1}>
                  <a href={channel.href}
                    {...(channel.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                    className="marketing-surface-card h-full block group hover:border-white/20 transition-colors">
                    <div className="flex items-center gap-3 mb-4">
                      <Icon className="h-6 w-6 text-[#ff6a1a]" />
                      <h3 className="text-xl font-semibold text-white">{channel.title}</h3>
                    </div>
                    <p className="text-sm leading-7 text-white/72">{channel.description}</p>
                    <p className="mt-4 text-sm font-semibold text-[#ff6a1a] group-hover:underline">{channel.value}</p>
                  </a>
                </ScrollReveal>
              )
            })}
          </div>
        </div>
      </section>

      <section className="marketing-section marketing-section-warm">
        <div className="container mx-auto px-6">
          <ScrollReveal direction="up">
            <div className="marketing-section-header">
              <div className="marketing-eyebrow marketing-eyebrow-dark">Company details</div>
              <h2 className="marketing-section-title marketing-section-title-dark">Business details.</h2>
            </div>
          </ScrollReveal>

          <ScrollReveal direction="up">
            <div className="mx-auto max-w-2xl rounded-[28px] border border-black/8 bg-white p-8 shadow-[0_30px_70px_rgba(10,10,10,0.08)]">
              <dl className="space-y-4 text-sm">
                <div className="flex justify-between border-b border-black/6 pb-3">
                  <dt className="font-semibold text-[#141414]">Company</dt>
                  <dd className="text-[#505050]">{props.details.company}</dd>
                </div>
                <div className="flex justify-between border-b border-black/6 pb-3">
                  <dt className="font-semibold text-[#141414]">Contact number</dt>
                  <dd className="text-[#505050]">{props.details.contact_number}</dd>
                </div>
                <div className="flex justify-between border-b border-black/6 pb-3">
                  <dt className="font-semibold text-[#141414]">Address</dt>
                  <dd className="text-right text-[#505050] max-w-xs">{props.details.address}</dd>
                </div>
                <div className="flex justify-between border-b border-black/6 pb-3">
                  <dt className="font-semibold text-[#141414]">Business hours</dt>
                  <dd className="text-[#505050]">{props.details.hours}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="font-semibold text-[#141414]">Response time</dt>
                  <dd className="text-[#505050]">{props.details.response_time}</dd>
                </div>
              </dl>
            </div>
          </ScrollReveal>
        </div>
      </section>

      <section className="marketing-section">
        <div className="container mx-auto px-6">
          <ScrollReveal direction="up">
            <div className="marketing-final-cta">
              <div>
                <div className="marketing-eyebrow">Get started</div>
                <h2 className="marketing-section-title">{props.cta.title}</h2>
                <p className="marketing-lead max-w-2xl">{props.cta.body}</p>
              </div>
              <div className="mt-8 flex flex-wrap gap-3">
                {props.cta.actions.map((action) => (
                  <a key={action.href} href={action.href} className="marketing-button-primary">
                    {action.label}
                  </a>
                ))}
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </>
  )
}
